import { computed, ref } from 'vue'

import { defineStore } from 'pinia'
import type { CommunityUserGroup } from '@communities/community-user-layer/types/community-user'
import type { IGroup, IGroupMember, IMembershipQuestions } from '../types/types'

/**
 * Groups UI Store - Handles UI-only state for groups
 * All data fetching is handled by the query store
 */
export const useGroupsUIStore = defineStore('groups-ui', () => {
  // ===== UI STATE =====
  const currentGroup = ref<IGroup | null>(null)
  const isCurrentGroupLoading = ref(false)
  const groupOwner = ref<IGroupMember | null>(null)
  const showGroupSwitcherDrawer = ref(false)
  const showGroupSettings = ref(false)
  const showMembershipQuestions = ref(false)
  const membershipQuestionsMode = ref<'answer' | 'view'>('answer')
  const groupMembers = ref<IGroupMember[]>([])
  const groupAdmins = ref<CommunityUserGroup[]>([])
  const isInitialized = ref(false)
  const isGroupInfoHidden = ref(false)
  const currentGroupMembershipQuestions = ref<IMembershipQuestions | null>(null)
  const showJoinGroupModal = ref(false)
  const showJoinSuccessConfetti = ref(false)
  /**
   * Order id captured from a successful paid group payment, kept here so it
   * survives the questionnaire detour. Mirrors the legacy ghl-revex-frontend
   * `GroupState.state.orderId` stash so the eventual `joinGroup` call (fired
   * from the membership-questions modal) can include the order context.
   */
  const pendingJoinOrderId = ref<string | null>(null)

  // ===== COMPUTED GETTERS =====
  const groupId = computed(() => currentGroup.value?._id)

  // ===== ACTIONS =====
  const setCurrentGroup = (group: IGroup | null) => {
    currentGroup.value = group
  }

  const setGroupOwner = (owner: IGroupMember | null) => {
    groupOwner.value = owner
  }

  const setCurrentGroupMembershipQuestions = (questions: IMembershipQuestions | null) => {
    currentGroupMembershipQuestions.value = questions
  }

  const setGroupMembers = (members: IGroupMember[]) => {
    groupMembers.value = members
  }

  const setGroupAdmins = (admins: CommunityUserGroup[]) => {
    groupAdmins.value = admins
  }

  const toggleGroupSwitcherDrawer = () => {
    showGroupSwitcherDrawer.value = !showGroupSwitcherDrawer.value
  }

  const toggleGroupSettings = () => {
    showGroupSettings.value = !showGroupSettings.value
  }

  const setShowGroupSettings = (value: boolean) => {
    showGroupSettings.value = value
  }

  const setShowMembershipQuestions = (value: boolean) => {
    showMembershipQuestions.value = value
  }

  const setMembershipQuestionsMode = (mode: 'answer' | 'view') => {
    membershipQuestionsMode.value = mode
  }

  const toggleMembershipQuestions = () => {
    showMembershipQuestions.value = !showMembershipQuestions.value
  }

  const setInitialized = (value: boolean) => {
    isInitialized.value = value
  }
  const setIsCurrentGroupLoading = (value: boolean) => {
    isCurrentGroupLoading.value = value
  }

  const toggleGroupInfo = () => {
    isGroupInfoHidden.value = !isGroupInfoHidden.value
  }

  const setGroupInfoHidden = (value: boolean) => {
    isGroupInfoHidden.value = value
  }

  const setShowJoinGroupModal = (value: boolean) => {
    showJoinGroupModal.value = value
  }

  const setShowJoinSuccessConfetti = (value: boolean) => {
    showJoinSuccessConfetti.value = value
  }

  const setPendingJoinOrderId = (value: string | null) => {
    pendingJoinOrderId.value = value
  }

  const reset = () => {
    currentGroup.value = null
    groupOwner.value = null
    showGroupSwitcherDrawer.value = false
    showGroupSettings.value = false
    showMembershipQuestions.value = false
    membershipQuestionsMode.value = 'answer'
    groupMembers.value = []
    groupAdmins.value = []
    isInitialized.value = false
    isGroupInfoHidden.value = false
    showJoinGroupModal.value = false
    showJoinSuccessConfetti.value = false
    pendingJoinOrderId.value = null
  }

  // ===== PUBLIC API =====
  return {
    // State
    currentGroup,
    isCurrentGroupLoading,
    groupOwner,
    showGroupSwitcherDrawer,
    showGroupSettings,
    showMembershipQuestions,
    membershipQuestionsMode,
    groupMembers,
    groupAdmins,
    isInitialized,
    isGroupInfoHidden,
    currentGroupMembershipQuestions,
    showJoinGroupModal,
    showJoinSuccessConfetti,
    pendingJoinOrderId,
    // Computed getters
    groupId,

    // Actions
    setCurrentGroup,
    setGroupOwner,
    setGroupMembers,
    setGroupAdmins,
    toggleGroupSwitcherDrawer,
    toggleGroupSettings,
    setShowGroupSettings,
    setShowMembershipQuestions,
    setMembershipQuestionsMode,
    toggleMembershipQuestions,
    setInitialized,
    reset,
    setIsCurrentGroupLoading,
    toggleGroupInfo,
    setGroupInfoHidden,
    setCurrentGroupMembershipQuestions,
    setShowJoinGroupModal,
    setShowJoinSuccessConfetti,
    setPendingJoinOrderId,
  }
})
